import {
  HTTP_INTERCEPTORS,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AppConfigService } from '../services/config.service';
import { ConsoleService } from '../services/console.service';
import { cloneDeepObj } from '../utils/util';

@Injectable()
export class PreventDuplicateRequestInterceptor implements HttpInterceptor {
  #lastRequest: string;
  #lastRequestBody: string;

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const duplicatedPaths = [
      { path: '/Payments/GetDescriptorOnlineGateway', method: 'GET' },
      {
        path: '/ScheduledMessages/CreateScheduledMessageByPhonebook',
        method: 'POST',
      },
      { path: '/ScheduledMessages/Create', method: 'POST' },
      { path: '/Payments/ConfirmPayment', method: 'POST' },
      { path: '/Payments/RejectPayment', method: 'POST' },
      { path: '/ScheduledMessages/CreateFastScheduledMessage', method: 'POST' },
      {
        path: '/ScheduledMessages/CreateScheduledMessageBulk2',
        method: 'POST',
      },
      { path: '/ScheduledMessages/CreateScheduledMessageBulk', method: 'POST' },
      {
        path: '/ScheduledMessages/CreateDynamicScheduledMessage',
        method: 'POST',
      },
      {
        path: '/ScheduledMessages/CreateTrackingNumberP2PScheduledMessage',
        method: 'POST',
      },
      { path: '/ScheduledMessages/CreateP2PScheduledMessage', method: 'POST' },
      {
        path: '/ScheduledMessages/CreateFastPatternScheduledMessageByPhonebook',
        method: 'POST',
      },
      {
        path: '/ScheduledMessages/CreateRegionalScheduledMessage',
        method: 'POST',
      },
      { path: '/OccasionScheduledMessages/Create', method: 'POST' },
      {
        path: '/SmartSmsRecipients/CreatePeriodicSmartSmsRecipient',
        method: 'POST',
      },
      { path: '/SmartSmsRecipients/CreateSmartSmsRecipient', method: 'POST' },
      {
        path: '/SmartSmsRecipients/CreateP2PSmartSmsRecipients',
        method: 'POST',
      },
    ];
    const urlWithoutDomain = request.url.replace(
      AppConfigService.config.apiUrl,
      '',
    );

    const currentPath = duplicatedPaths.find(
      (item) =>
        item.path === urlWithoutDomain && item.method === request.method,
    );

    const payload = this.getPayload(request);

    // If monitored and matches previous → block
    if (
      currentPath &&
      urlWithoutDomain === this.#lastRequest &&
      payload === this.#lastRequestBody
    ) {
      ConsoleService.info('Prevent duplicate request');
      return EMPTY;
    }

    // Save last request for duplicate detection
    if (currentPath) {
      this.#lastRequest = urlWithoutDomain;
      this.#lastRequestBody = cloneDeepObj(request.body);
    }

    return next.handle(request).pipe(
      tap({
        next: () => {
          // Do nothing on success (duplicates remain blocked)
        },
        error: () => {
          // Allow retry ONLY for ConfirmPayment
          if (urlWithoutDomain === '/Payments/ConfirmPayment') {
            this.#lastRequest = null;
            this.#lastRequestBody = null;
            ConsoleService.info('ConfirmPayment failed → allow retry');
          }
        },
      }),
    );
  }

  private getPayload(request: HttpRequest<any>) {
    if (request.method === 'GET') {
      return request.urlWithParams;
    }
    return JSON.stringify(request.body);
  }
}

export const HttpPreventDuplicateRequestInterceptorProviders = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: PreventDuplicateRequestInterceptor,
    multi: true,
  },
];
